import Layout from "../../components/feature/Layout";
import ClientsTable from "./components/ClientsTable";
import ClientStats from "./components/ClientStats";
import ClientFilters from "./components/ClientFilters";
import { useState } from "react";

export default function CRM() {
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showClientDetailModal, setShowClientDetailModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'general' | 'contacts' | 'operations' | 'finances' | 'documents' | 'notes'>('general');

  const showToast = (message: string) => {
    setToastMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const handleSelectClient = (clientId: string) => {
    setSelectedClient(clientId);
    setShowClientDetailModal(true);
    setActiveTab('general');
  };

  const handleCreateClient = () => {
    setShowNewClientModal(false);
    showToast('Cliente registrado exitosamente');
  };

  // Datos del cliente seleccionado
  const clientData = {
    id: selectedClient || 'CLI-001',
    name: 'Importadora del Pacífico S.A.',
    commercialName: 'Pacífico Import',
    nit: 'IDN850623AB1',
    type: 'Importador',
    status: 'Activo',
    country: 'Guatemala',
    email: 'contacto@pacifico.com',
    phone: '+502 2345-6789',
    address: 'Av. Las Américas 15-45, Zona 13, Ciudad de Guatemala',
    executive: 'Juan Delgado',
    creditRisk: 'Bajo',
    creditLimit: '$500,000',
    balance: '$125,400',
    contacts: [
      { name: 'María González', position: 'Gerente de Logística', phone: '+502 2345-6789', email: 'mgonzalez@pacifico.com', isPrimary: true },
      { name: 'Laura Morales', position: 'Asistente Administrativa', phone: '+502 2789-0123', email: 'lmorales@pacifico.com', isPrimary: false },
    ],
    operations: [
      { id: 'OP-2024-0156', type: 'Importación Marítima', status: 'En Tránsito', value: '$45,280', date: '2024-01-15', aduana: 'Santo Tomás de Castilla' },
      { id: 'OP-2024-0142', type: 'Importación Aérea', status: 'Completada', value: '$38,900', date: '2024-01-10', aduana: 'La Aurora' },
      { id: 'OP-2024-0128', type: 'Importación Terrestre', status: 'Completada', value: '$62,150', date: '2024-01-05', aduana: 'El Carmen' },
    ],
    finances: {
      totalInvoices: 12,
      totalPaid: '$425,600',
      pending: '$125,400',
      overdue: '$0',
      aging: [
        { range: '0-30 días', amount: '$85,400' },
        { range: '31-60 días', amount: '$40,000' },
        { range: '61-90 días', amount: '$0' },
        { range: '90+ días', amount: '$0' },
      ]
    },
    documents: [
      { name: 'Acta Constitutiva', type: 'Legal', date: '2023-06-15', status: 'Vigente' },
      { name: 'Poder Notarial', type: 'Legal', date: '2023-06-15', status: 'Vigente' },
      { name: 'RTU', type: 'Fiscal', date: '2024-01-01', status: 'Vigente' },
      { name: 'Patente de Comercio', type: 'Legal', date: '2023-06-15', status: 'Vigente' },
    ],
    notes: [
      { date: '2024-01-20', user: 'Juan Delgado', note: 'Cliente solicita cotización para nueva operación de maquinaria industrial.' },
      { date: '2024-01-15', user: 'María López', note: 'Pago recibido por operación OP-2024-0142. Cliente al día en sus pagos.' },
      { date: '2024-01-10', user: 'Juan Delgado', note: 'Reunión con cliente para revisar condiciones comerciales del próximo trimestre.' },
    ]
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Clientes</h1>
            <p className="text-sm text-gray-500 mt-1">Gestión de clientes y cuentas</p>
          </div>
          <button 
            onClick={() => setShowNewClientModal(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-add-line text-lg"></i>
            Nuevo Cliente
          </button>
        </div>

        <ClientStats />
        <ClientFilters />
        <ClientsTable onSelectClient={handleSelectClient} />
      </div>

      {/* Modal Nuevo Cliente */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2A43]">Registrar Nuevo Cliente</h2>
              <button onClick={() => setShowNewClientModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Razón Social *</label>
                  <input type="text" placeholder="Nombre legal de la empresa" className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Nombre Comercial</label>
                  <input type="text" placeholder="Nombre comercial" className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">NIT *</label>
                  <input type="text" placeholder="Número de identificación tributaria" className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Tipo de Cliente *</label>
                  <select className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent cursor-pointer">
                    <option>Importador</option>
                    <option>Exportador</option>
                    <option>Ambos</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">País *</label>
                  <select className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent cursor-pointer">
                    <option>Guatemala</option>
                    <option>El Salvador</option>
                    <option>Honduras</option>
                    <option>Nicaragua</option>
                    <option>Costa Rica</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Email *</label>
                  <input type="email" placeholder="correo@empresa.com" className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Teléfono</label>
                  <input type="tel" placeholder="+502 2345-6789" className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Dirección Fiscal</label>
                  <textarea rows={2} placeholder="Dirección completa" className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent resize-none"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Ejecutivo Asignado *</label>
                  <select className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent cursor-pointer">
                    <option>Juan Delgado</option>
                    <option>María López</option>
                    <option>Carlos Méndez</option>
                    <option>Ana Rodríguez</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Riesgo Crediticio</label>
                  <select className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent cursor-pointer">
                    <option>Bajo</option>
                    <option>Medio</option>
                    <option>Alto</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button onClick={() => setShowNewClientModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button onClick={handleCreateClient} className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap">
                Registrar Cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ficha de Cliente */}
      {showClientDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">{clientData.name}</h2>
                  <p className="text-sm text-white/80 mt-1">{clientData.id} • {clientData.nit}</p>
                </div>
                <button onClick={() => setShowClientDetailModal(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/20 cursor-pointer">
                  <i className="ri-close-line text-xl text-white"></i>
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <span className="px-2.5 py-1 text-xs font-medium bg-white/20 text-white rounded-md">{clientData.type}</span>
                <span className="px-2.5 py-1 text-xs font-medium bg-[#10B981] text-white rounded-md">{clientData.status}</span>
                <span className="px-2.5 py-1 text-xs font-medium bg-white/20 text-white rounded-md">Riesgo: {clientData.creditRisk}</span>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-gray-200 bg-white sticky top-[120px] z-10">
              <div className="flex gap-1 px-6">
                {[
                  { id: 'general', label: 'Información General', icon: 'ri-information-line' },
                  { id: 'contacts', label: 'Contactos', icon: 'ri-contacts-line' },
                  { id: 'operations', label: 'Operaciones', icon: 'ri-ship-line' },
                  { id: 'finances', label: 'Finanzas', icon: 'ri-money-dollar-circle-line' },
                  { id: 'documents', label: 'Documentos', icon: 'ri-file-text-line' },
                  { id: 'notes', label: 'Notas Internas', icon: 'ri-sticky-note-line' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'border-[#2F80ED] text-[#2F80ED]'
                        : 'border-transparent text-gray-500 hover:text-[#0A2A43]'
                    }`}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Tab: Información General */}
              {activeTab === 'general' && (
                <div className="grid grid-cols-3 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-[#0A2A43] uppercase">Datos Básicos</h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Razón Social</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.name}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Nombre Comercial</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.commercialName}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">NIT</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.nit}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">País</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.country}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-[#0A2A43] uppercase">Contacto</h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.email}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Teléfono</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.phone}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Dirección Fiscal</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.address}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-[#0A2A43] uppercase">Información Comercial</h3>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Ejecutivo Asignado</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.executive}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Riesgo Crediticio</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.creditRisk}</p>
                      </div>
                      <div className="p-3 bg-[#EAF2F8] rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Saldo Actual</p>
                        <p className="text-lg font-bold text-[#2F80ED]">{clientData.balance}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Total Operaciones</p>
                        <p className="text-sm font-medium text-[#0A2A43]">{clientData.operations.length}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Contactos */}
              {activeTab === 'contacts' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-[#0A2A43]">Contactos del Cliente</h3>
                    <button className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all text-sm whitespace-nowrap cursor-pointer">
                      <i className="ri-add-line mr-2"></i>Agregar Contacto
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {clientData.contacts.map((contact, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#114B7A] to-[#2F80ED] flex items-center justify-center text-white font-semibold">
                              {contact.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-[#0A2A43]">{contact.name}</p>
                              <p className="text-xs text-gray-500">{contact.position}</p>
                              {contact.isPrimary && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-[#2F80ED] text-white text-xs rounded-full">Principal</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-[#0A2A43]">
                            <i className="ri-phone-line text-gray-400"></i>
                            {contact.phone}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#0A2A43]">
                            <i className="ri-mail-line text-gray-400"></i>
                            {contact.email}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab: Operaciones */}
              {activeTab === 'operations' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-[#0A2A43]">Historial de Operaciones</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#EAF2F8]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase">Operación</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase">Aduana</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase">Fecha</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {clientData.operations.map((op, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-[#0A2A43]">{op.id}</td>
                            <td className="px-4 py-3 text-sm text-[#0A2A43]">{op.type}</td>
                            <td className="px-4 py-3 text-sm text-[#0A2A43]">{op.aduana}</td>
                            <td className="px-4 py-3 text-sm text-[#0A2A43]">{op.date}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-[#0A2A43]">{op.value}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                op.status === 'En Tránsito' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {op.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab: Finanzas */}
              {activeTab === 'finances' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-[#EAF2F8] rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Total Facturado</p>
                      <p className="text-2xl font-bold text-[#0A2A43]">{clientData.finances.totalPaid}</p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Saldo Pendiente</p>
                      <p className="text-2xl font-bold text-[#EAB308]">{clientData.finances.pending}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Saldo Vencido</p>
                      <p className="text-2xl font-bold text-[#10B981]">{clientData.finances.overdue}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Total Facturas</p>
                      <p className="text-2xl font-bold text-[#0A2A43]">{clientData.finances.totalInvoices}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0A2A43] mb-4">Antigüedad de Saldos</h3>
                    <div className="space-y-3">
                      {clientData.finances.aging.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-[#0A2A43]">{item.range}</span>
                          <span className="text-sm font-bold text-[#0A2A43]">{item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Documentos */}
              {activeTab === 'documents' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-[#0A2A43]">Documentos del Cliente</h3>
                    <button className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all text-sm whitespace-nowrap cursor-pointer">
                      <i className="ri-upload-line mr-2"></i>Subir Documento
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {clientData.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#EAF2F8]">
                            <i className="ri-file-text-line text-xl text-[#2F80ED]"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[#0A2A43]">{doc.name}</p>
                            <p className="text-xs text-gray-500">{doc.type} • {doc.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">{doc.status}</span>
                          <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-200 transition-colors cursor-pointer">
                            <i className="ri-download-line text-[#2F80ED]"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab: Notas Internas */}
              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-[#0A2A43]">Notas Internas</h3>
                    <button className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all text-sm whitespace-nowrap cursor-pointer">
                      <i className="ri-add-line mr-2"></i>Agregar Nota
                    </button>
                  </div>
                  <div className="space-y-3">
                    {clientData.notes.map((note, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-[#0A2A43]">{note.user}</span>
                          <span className="text-xs text-gray-500">{note.date}</span>
                        </div>
                        <p className="text-sm text-[#0A2A43]">{note.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-between border-t border-gray-200">
              <button className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-close-circle-line mr-2"></i>Inactivar Cliente
              </button>
              <div className="flex gap-3">
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-file-download-line mr-2"></i>Estado de Cuenta
                </button>
                <button className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-edit-line mr-2"></i>Editar Cliente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {showSuccessToast && (
        <div className="fixed bottom-6 right-6 bg-[#10B981] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 z-50">
          <i className="ri-check-circle-line text-xl"></i>
          <span className="font-medium">{toastMessage}</span>
        </div>
      )}
    </Layout>
  );
}
